/**
 * Customer Referral Dashboard Screen
 *
 * Shows the user's referral code, share options, and referral status list.
 * Reuses ReferralLinkBox from partner-dashboard for the share UI.
 *
 * Task: NocoDB #1554 — Referral dashboard + share components in app
 * Epic: #1529 — DD Affiliate + Customer Referral Program
 */

import React, { useCallback, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Stack } from 'expo-router';
import { useTranslation } from '@dramadetector/shared';
import { trackEvent } from '@/lib/analytics/events';
import { GlassBackground } from '../../components/glass/GlassBackground';
import { GlassCard } from '../../components/glass/GlassCard';
import { GlassButton } from '../../components/glass';
import { useCustomerReferral } from '../../hooks/useCustomerReferral';
import { ReferralLinkBox } from '../../components/screens/partner-dashboard/ReferralLinkBox';
import { styles, getStatusStyle } from '../../components/styles/referrals.styles';

export default function ReferralsScreen() {
  const { t } = useTranslation('common');

  const {
    referralCode,
    shortUrl,
    referrals,
    totalReferrals,
    conversions,
    isLoading,
    generateCode,
    isGenerating,
    copyReferralLink,
    shareReferralLink,
  } = useCustomerReferral();

  useEffect(() => {
    trackEvent('Screen_View_Referrals', {});
  }, []);

  const handleCopy = useCallback(async () => {
    trackEvent('Button_Click_CopyReferralLink', { source: 'customer' });
    await copyReferralLink();
  }, [copyReferralLink]);

  const handleShare = useCallback(async () => {
    trackEvent('Button_Click_ShareReferralLink', { source: 'customer', method: 'native' });
    await shareReferralLink();
  }, [shareReferralLink]);

  const handleShowQR = useCallback(() => {
    trackEvent('Button_Click_ShareReferralLink', { source: 'customer', method: 'qr' });
  }, []);

  if (isLoading) {
    return (
      <GlassBackground>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.center}>
          <ActivityIndicator size="large" color="#8B5CF6" />
        </View>
      </GlassBackground>
    );
  }

  return (
    <GlassBackground>
      <Stack.Screen options={{ headerShown: false }} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{t('referrals.title')}</Text>
          <Text style={styles.subtitle}>
            {t('referrals.subtitle')}
          </Text>
        </View>

        {/* Referral Code Section */}
        {referralCode ? (
          <View style={styles.section}>
            <ReferralLinkBox
              shortLink={shortUrl ?? ''}
              onCopy={handleCopy}
              onShareEmail={handleShare}
              onShareSMS={handleShare}
              onShowQR={handleShowQR}
              title={t('referrals.your_link')}
              copyAccessibilityLabel={t('referrals.copy_link')}
              shareEmailLabel={t('referrals.share_email')}
              shareSMSLabel={t('referrals.share_sms')}
              shareQRLabel={t('referrals.share_qr')}
            />
          </View>
        ) : (
          <View style={styles.section}>
            <GlassCard>
              <Text style={styles.generateText}>
                {t('referrals.generate_prompt')}
              </Text>
              <GlassButton
                variant="accent"
                onPress={generateCode}
                disabled={isGenerating}
                testID="generate-referral-code"
              >
                {isGenerating
                  ? t('referrals.generating')
                  : t('referrals.generate_code')}
              </GlassButton>
            </GlassCard>
          </View>
        )}

        {/* Stats */}
        <View style={styles.section}>
          <GlassCard>
            <View style={styles.statsRow}>
              <View style={styles.stat}>
                <Text style={styles.statValue}>{totalReferrals}</Text>
                <Text style={styles.statLabel}>
                  {t('referrals.stat_invited')}
                </Text>
              </View>
              <View style={styles.statDivider} />
              <View style={styles.stat}>
                <Text style={styles.statValue}>{conversions}</Text>
                <Text style={styles.statLabel}>
                  {t('referrals.stat_converted')}
                </Text>
              </View>
            </View>
          </GlassCard>
        </View>

        {/* Referral List */}
        {referrals.length > 0 && (
          <View style={styles.section}>
            <GlassCard>
              <Text style={styles.sectionTitle}>
                {t('referrals.activity')}
              </Text>
              {referrals.map((ref) => (
                <View key={ref.id} style={styles.referralRow}>
                  <View style={styles.referralInfo}>
                    <Text style={styles.referralEmail} numberOfLines={1}>
                      {ref.referredEmail ?? t('referrals.pending_signup')}
                    </Text>
                    <Text style={styles.referralDate}>
                      {ref.displayDate}
                    </Text>
                  </View>
                  <View style={[
                    styles.statusBadge,
                    getStatusStyle(ref.status),
                  ]}>
                    <Text style={styles.statusText}>
                      {t(`referrals.status_${ref.status}`)}
                    </Text>
                  </View>
                </View>
              ))}
            </GlassCard>
          </View>
        )}
      </ScrollView>
    </GlassBackground>
  );
}
